import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../contexts/PopupContext';
import Modal from '../layout/Modal';

// Import różnych typów popupów
import ProfilePopup from './ProfilePopup';
import EventDetailsPopup from './EventDetailsPopup';
import SettingsPopup from './SettingsPopup';
import EditProfilePopup from './EditProfilePopup';
import DiscoverListPopup from './DiscoverListPopup';
import DiscoverMapPopup from './DiscoverMapPopup';
import CreateEventPopup from './CreateEventPopup';
import EditEventPopup from './EditEventPopup';
import EventFiltersPopup from '../events/EventFiltersPopup';
import ShareEventPopup from './ShareEventPopup';
import { FriendsManagementPopup } from '../profile/friends';
import AchievementsModal from './AchievementsModal';

// Import komponentów settings
import {
  AccountSettings,
  PrivacySettings,
  NotificationSettings,
  AppearanceSettings,
  AboutSettings,
  HelpCenterSettings,
  BillingSettings,
} from '../profile/settings';

// Import formularzy AccountSettings
import ChangeEmailForm from '../profile/settings/ChangeEmailForm';
import ResetPasswordForm from '../profile/settings/ResetPasswordForm';
import ChangePhoneForm from '../profile/settings/ChangePhoneForm';
import DeactivateAccountForm from '../profile/settings/DeactivateAccountForm';
import DeleteAccountForm from '../profile/settings/DeleteAccountForm';

function PopupManager() {
  const { t } = useTranslation(['profile', 'discover', 'events', 'shared']);
  
  const { popups, closePopup } = usePopup();
  const settingsSubpageTypes = new Set([
    'settings-account',
    'settings-privacy',
    'settings-notifications',
    'settings-appearance',
    'settings-about',
    'settings-help',
    'settings-billing',
    'account-change-email',
    'account-reset-password',
    'account-change-phone',
    'account-deactivate',
    'account-delete',
  ]);

  const renderPopupContent = (popup) => {
    switch (popup.type) {
      case 'profile':
        return <ProfilePopup userId={popup.data.userId} onClose={() => closePopup(popup.id)} />;
      case 'event-details':
        return <EventDetailsPopup eventId={popup.data.eventId} onClose={() => closePopup(popup.id)} />;
      case 'settings':
        return <SettingsPopup onLogout={popup.data?.onLogout} />;
      case 'edit-profile':
        return <EditProfilePopup onClose={() => closePopup(popup.id)} />;
      case 'discover-list':
        return <DiscoverListPopup />;
      case 'discover-map-list':
        return <DiscoverListPopup events={popup.data?.events || null} />;
      case 'discover-map':
        return <DiscoverMapPopup onClose={() => closePopup(popup.id)} />;
      case 'create-event':
        return <CreateEventPopup onClose={() => closePopup(popup.id)} onSuccess={popup.data.onSuccess} />;
      case 'edit-event':
        return <EditEventPopup event={popup.data.event} onClose={() => closePopup(popup.id)} onSuccess={popup.data.onSuccess} />;
      case 'event-filters':
        return <EventFiltersPopup 
          onClose={() => closePopup(popup.id)} 
          initialFilters={popup.data?.initialFilters || {}}
          onApply={popup.data?.onApply}
        />;
      // Settings subpages
      case 'settings-account':
        return <AccountSettings />;
      case 'settings-privacy':
        return <PrivacySettings />;
      case 'settings-notifications':
        return <NotificationSettings />;
      case 'settings-appearance':
        return <AppearanceSettings />;
      case 'settings-about':
        return <AboutSettings />;
      case 'settings-help':
        return <HelpCenterSettings />;
      case 'settings-billing':
        return <BillingSettings />;
      // Account settings forms
      case 'account-change-email':
        return <ChangeEmailForm onClose={() => closePopup(popup.id)} currentEmail={popup.data.currentEmail} />;
      case 'account-reset-password':
        return <ResetPasswordForm onClose={() => closePopup(popup.id)} userEmail={popup.data.userEmail} />;
      case 'account-change-phone':
        return <ChangePhoneForm onClose={() => closePopup(popup.id)} currentPhone={popup.data.currentPhone} />;
      case 'account-deactivate':
        return <DeactivateAccountForm onClose={() => closePopup(popup.id)} />;
      case 'account-delete':
        return <DeleteAccountForm onClose={() => closePopup(popup.id)} />;
      case 'friends-management':
        return <FriendsManagementPopup onClose={() => closePopup(popup.id)} />;
      case 'share-event':
        return (
          <ShareEventPopup
            eventId={popup.data.eventId}
            eventTitle={popup.data.eventTitle}
            onClose={() => closePopup(popup.id)}
          />
        );
      default:
        return <div>Unknown popup type: {popup.type}</div>;
    }
  };

  const getPopupTitle = (type) => {
    const titles = {
      'profile': t('profile.title'),
      'event-details': t('events.details.title', 'Szczegóły wydarzenia'),
      'settings': t('profile.subTabs.settings'),
      'edit-profile': t('profile.subTabs.edit'),
      'discover-list': t('discover.list.title'),
      'discover-map-list': t('discover.list.title'),
      'discover-map': t('discover.map.title', 'Mapa wydarzeń'),
      'create-event': t('events.createEvent.title'),
      'edit-event': t('events.editEvent.title', 'Edytuj wydarzenie'),
      'event-filters': t('events.filters.title', 'Filtruj wydarzenia'),
      // Settings subpages
      'settings-account': t('profile.settings.menu.account'),
      'settings-privacy': t('profile.settings.menu.privacy'),
      'settings-notifications': t('profile.settings.menu.notifications'),
      'settings-appearance': t('profile.settings.menu.appearance', 'Wygląd'),
      'settings-about': t('profile.settings.menu.about'),
      'settings-help': t('profile.settings.menu.help'),
      'settings-billing': t('profile.settings.menu.billing'),
      // Account settings forms
      'account-change-email': t('profile.settings.pages.account.changeEmailForm.title'),
      'account-reset-password': t('profile.settings.pages.account.resetPasswordForm.title'),
      'account-change-phone': t('profile.settings.pages.account.changePhoneForm.title'),
      'account-deactivate': t('profile.settings.pages.account.deactivateForm.title'),
      'account-delete': t('profile.settings.pages.account.deleteForm.title'),
      'friends-management': t('profile.friends.title'),
    };
    return titles[type] || 'Popup';
  };

  return (
    <>
      {popups.map((popup) => {
        if (popup.type === 'achievements') {
          return (
            <AchievementsModal
              key={popup.id}
              popup={popup}
              onClose={() => closePopup(popup.id)}
            />
          );
        }

        return (
          <Modal
            key={popup.id}
            isOpen={popup.isOpen}
            onClose={() => closePopup(popup.id)}
            title={popup.type === 'event-details' || popup.type === 'profile' || popup.type === 'share-event' ? null : getPopupTitle(popup.type)}
            priority={popup.priority}
            className={
              popup.type === 'event-details' || popup.type === 'discover-map'
                ? 'modal-content-wide'
                : popup.type === 'discover-list' || popup.type === 'discover-map-list'
                  ? 'modal-content-discover-events-list'
                  : popup.type === 'create-event' ||
                      popup.type === 'edit-event' ||
                      popup.type === 'edit-profile'
                    ? 'modal-content-create-event'
                    : popup.type === 'settings'
                      ? 'modal-content-settings'
                      : popup.type === 'profile'
                        ? 'modal-content-profile'
                        : settingsSubpageTypes.has(popup.type)
                          ? 'modal-content-settings-subpage'
                          : popup.type === 'friends-management'
                            ? 'modal-content-friends'
                            : ''
            }
          >
            {renderPopupContent(popup)}
          </Modal>
        );
      })}
    </>
  );
}

export default PopupManager;

